import { useMemo } from 'react';
import { useRealtimeData } from './useFirebaseData';
import { COLLECTIONS, STUDENT_STATUS } from '../utils/constants';
import { formatStudentForDisplay } from '../utils/studentUtils';
import { formatClassForDisplay, sortClassesByYear } from '../utils/classUtils';
import { useSchool } from '../contexts/SchoolContext';

export const useSchoolData = () => {
  const { loading: schoolLoading, permissions } = useSchool();

  // Fetch students and classes from Firebase using real-time listeners
  // schoolId filtering is now handled automatically by useRealtimeData
  // Empty data is returned if user has no schoolId
  const {
    data: rawStudents,
    loading: studentsLoading,
    error: studentsError
  } = useRealtimeData(COLLECTIONS.STUDENTS);

  const {
    data: rawClasses,
    loading: classesLoading,
    error: classesError
  } = useRealtimeData(COLLECTIONS.CLASSES);

  // Existing saved rosters can reference learners who have since left. Keep
  // their already-authorised records available without changing current pickers.
  const allStudents = useMemo(() => (Array.isArray(rawStudents) ? rawStudents : [])
    .map(formatStudentForDisplay).filter(Boolean), [rawStudents]);

  // Process and format data
  const students = useMemo(() => {
    if (!rawStudents || !Array.isArray(rawStudents)) return [];

    return rawStudents
      .filter(student => {
        if (!student) return false;
        // Filter by status field (new approach)
        // If status exists, must be 'active'
        // If no status, fall back to active !== false for backwards compatibility during migration
        return student.status ? student.status === STUDENT_STATUS.ACTIVE : student.active !== false;
      })
      .map(formatStudentForDisplay)
      .filter(Boolean); // Remove null/undefined entries
  }, [rawStudents]);

  const classes = useMemo(() => {
    if (!rawClasses || !Array.isArray(rawClasses)) return [];

    return sortClassesByYear(
      rawClasses
        .filter(cls => {
          if (!cls || cls.active === false) return false;

          // Filter out sample classes for non-dev users
          if (!permissions?.dev) {
            const className = (cls.name || '').toLowerCase();
            if (className.includes('sample')) {
              return false;
            }
          }

          return true;
        })
        .map(formatClassForDisplay)
        .filter(Boolean)
    );
  }, [rawClasses, permissions]);

  // Loading states (include school loading for proper initialization)
  const isLoading = schoolLoading || studentsLoading || classesLoading;
  const isDataLoaded = !isLoading && students.length >= 0 && classes.length >= 0;

  // Error handling
  const error = studentsError || classesError;


  // Statistics
  const stats = useMemo(() => {
    return {
      totalStudents: students.length,
      totalClasses: classes.length,
      studentsByClass: students.reduce((acc, student) => {
        const className = student.className || 'Unassigned';
        acc[className] = (acc[className] || 0) + 1;
        return acc;
      }, {}),
      classesByYear: classes.reduce((acc, cls) => {
        const year = cls.year || 'Unknown';
        acc[year] = (acc[year] || 0) + 1;
        return acc;
      }, {})
    };
  }, [students, classes]);

  return {
    students,
    allStudents,
    classes,
    isLoading,
    isDataLoaded,
    error,
    stats,
    // Individual loading states for granular control
    studentsLoading,
    classesLoading,
    studentsError,
    classesError
  };
};
